<script setup lang="ts">
  import { computed } from 'vue'
  import type { ComboboxFieldProps } from '@gat-ship/vue-components'
  import {
    Button,
    ComboboxField,
    useAppLoading
  } from '@gat-ship/vue-components'
  import {
    IconLoading,
    IconTerminal,
    IconChevronDown,
    IconPlus,
    IconEdit
  } from '@gat-ship/icons'
  import type { AiPromptAsset } from '../model/types'
  import AiThinkingModelIcon from './AiThinkingModelIcon.vue'

  export interface SelectPromptAssetProps extends Omit<
    ComboboxFieldProps,
    'items' | 'modelValue'
  > {
    assets: AiPromptAsset[]
    canManageAssets: boolean
  }

  const props = defineProps<SelectPromptAssetProps>()

  const selectedAssetId = defineModel<string | undefined>('selectedAssetId', {
    required: true
  })

  const emit = defineEmits<{
    (e: 'add-asset'): void
    (e: 'edit-asset', asset: AiPromptAsset): void
  }>()

  const { isLoading: isLoadingApp } = useAppLoading()

  const _items = computed(() =>
    props.assets.map((asset) => ({
      title: asset.displayName,
      value: asset.id,
      metaData: asset
    }))
  )

  const isLoading = computed(() => isLoadingApp('smart-paste-assets'))
</script>

<template>
  <ComboboxField
    v-model="selectedAssetId"
    :popover-props="{ width: 470, class: 'min-w-[400px]' }"
    :disabled="isLoading"
    required
    :items="_items"
  >
    <template v-if="isLoading" #content>
      <div class="flex items-center gap-2 p-2 text-sm">
        <IconLoading class="size-4" /> Loading presets..
      </div>
    </template>
    <template #trigger="{ selectedTitle }">
      <div
        class="flex h-[36px] w-fit cursor-pointer gap-1.5 rounded-lg pl-2 pr-2.5 text-[#0285ff] hover:bg-[#e5f3ff]"
      >
        <div class="shrink-0 self-center pb-0.5">
          <IconLoading v-if="isLoading" class="size-4" />
          <IconTerminal v-else class="size-5" :stroke-width="2" />
        </div>
        <div class="max-w-[200px] grow self-center truncate text-sm">
          {{ isLoading ? 'Loading...' : selectedTitle }}
        </div>
        <div v-if="!isLoading" class="shrink-0 self-center">
          <IconChevronDown class="size-4" />
        </div>
      </div>
    </template>
    <template #prepend-content-item>
      <div class="flex items-center gap-1 px-2 py-2 text-xs text-neutral-700">
        <span>Prompt Preset</span>
        <Button
          v-if="canManageAssets"
          variant="ghost"
          class="ml-auto h-6 w-fit"
          @click.stop="emit('add-asset')"
        >
          <IconPlus class="size-4" />
          Add
        </Button>
      </div>
    </template>
    <template #item="{ item }">
      <div class="flex w-full items-center">
        <div class="flex min-w-0 grow flex-col gap-1 overflow-hidden">
          <div class="flex min-w-0 grow flex-col gap-1 overflow-hidden">
            <div class="w-full truncate font-medium">
              {{ item.metaData.displayName }}
            </div>
            <div
              class="text-muted-foreground w-full truncate text-xs font-normal"
            >
              {{ item.metaData.shortDescription }}
            </div>
          </div>
          <div v-if="canManageAssets" class="flex flex-wrap items-center gap-1">
            <div
              v-if="item.metaData.targetAiModel !== 'default'"
              class="text-muted-foreground flex w-fit items-center gap-0.5 rounded-md bg-purple-100 px-2 py-1 text-xs font-normal capitalize"
            >
              <AiThinkingModelIcon :ai-model="item.metaData.targetAiModel" />
              {{ item.metaData.targetAiModel }}
            </div>
            <div
              v-if="item.metaData.isDefault"
              class="text-muted-foreground flex w-fit items-center gap-0.5 rounded-md bg-green-100 px-2 py-1 text-xs font-normal"
            >
              Default
            </div>
          </div>
        </div>
        <div class="w-10 min-w-10 shrink-0">
          <IconLoading v-if="isLoadingApp(item.metaData.id)" class="size-4" />
          <Button
            v-else-if="canManageAssets"
            variant="outline"
            size="icon"
            @click.stop="emit('edit-asset', item.metaData)"
          >
            <IconEdit class="size-4" />
          </Button>
        </div>
      </div>
    </template>
  </ComboboxField>
</template>
